import { useEffect } from 'react';

/**
 * Sistema de reveal on scroll.
 * Reemplaza a animate.css: no hace falta la dependencia externa del CDN.
 *
 * Uso: llamar una vez en la página. Marcar los elementos con
 *   className="reveal reveal-up"   (o reveal-left / reveal-right / reveal-scale / reveal-fade)
 * y opcionalmente agrupar en un contenedor con data-stagger="70".
 *
 * Respeta prefers-reduced-motion y tiene un failsafe: si el observer no
 * dispara en 2.5s, muestra todo igual (nunca queda contenido invisible).
 */
export default function useReveal(deps = []) {
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const items = document.querySelectorAll('.reveal:not(.is-in)');
    if (!items.length) return undefined;

    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }

    document.querySelectorAll('[data-stagger]').forEach((group) => {
      const step = parseInt(group.dataset.stagger, 10) || 70;
      Array.from(group.querySelectorAll('.reveal')).forEach((el, i) => {
        if (!el.dataset.delay) el.dataset.delay = String(Math.min(i, 5) * step);
      });
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.style.transitionDelay = `${parseInt(el.dataset.delay || '0', 10)}ms`;
          el.classList.add('is-in');
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    items.forEach((el) => io.observe(el));

    const failsafe = setTimeout(() => {
      items.forEach((el) => el.classList.add('is-in'));
    }, 2500);

    return () => {
      clearTimeout(failsafe);
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
